import React, { useCallback, useState } from 'react'
import { Settings, LogOut, RefreshCw, Globe, Check, CloudUpload, ExternalLink, Monitor, Megaphone } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../hooks/useAuth'
import { useLanguage } from '../../../contexts/LanguageProvider'
import { usePreferencesDialog } from '../../../contexts/PreferencesDialogContext'
import { usePlatform } from '../../../hooks/usePlatform'
import { useShortcutText } from '../../../hooks/useShortcutText'
import { checkForUpdates } from '../../../hooks/useUpdateStatus'
import { openFeedbackForm } from '../../../lib/sentry'
import { HBaseIcon } from '../../icons/HBaseIcon'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../../ui/dropdown-menu'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { WEB_APP_URL } from '@/src/shared/webAppUrl'

const DOWNLOAD_URL = 'https://happytask.app'

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'de', name: 'Deutsch' },
] as const
// Shared default profile picture — the same asset ProfilePictureIcon shows in
// the Settings profile section, so the sidebar falls back to the real default
// (not the legacy Sidebar dragon) when no avatar has been chosen.
import avatarDefault from '../../../assets/images/setupProfileDefaultPicture.png'
import avatarDefault2x from '../../../assets/images/setupProfileDefaultPicture@2x.png'
import './UserAvatar.scss'

export interface UserAvatarUser {
  full_name?: string
  email?: string
  avatar_url?: string
  user_metadata?: {
    full_name?: string
    avatar_url?: string
    [key: string]: unknown
  }
}

export interface UserAvatarProps {
  user: UserAvatarUser | null | undefined
  avatarUrl?: string | null
  fullName?: string | null
  /**
   * `true` until the avatar fetch resolves. While loading we suppress the
   * <img> entirely so the user never sees the bundled fallback PNG flash
   * before their real picture arrives.
   */
  isLoading?: boolean
}

/**
 * User profile avatar button with dropdown menu in the sidebar footer.
 *
 * Built on the shadcn/Radix DropdownMenu primitive — open/close state is
 * managed internally by Radix.
 */
const UserAvatarComponent: React.FC<UserAvatarProps> = ({
  user,
  avatarUrl,
  fullName,
  isLoading = false,
}) => {
  const { t } = useTranslation()
  const { language: activeLanguage, changeLanguage } = useLanguage()
  const navigate = useNavigate()
  const { user: authUser, logout, isAuthenticated } = useAuth()
  const preferencesDialog = usePreferencesDialog()
  const { isWeb } = usePlatform()
  const shortcutText = useShortcutText()

  const handleSettings = useCallback(() => {
    if (window.api?.invoke) {
      window.api.invoke('preferences:open')
    } else if (preferencesDialog) {
      preferencesDialog.open()
    }
  }, [preferencesDialog])

  const handleCheckForUpdates = useCallback(() => {
    checkForUpdates()
  }, [])

  // Der Weg zur jeweils anderen Plattform. Beide Adressen stehen fest: die
  // Web-App und die Seite, auf der später die Promo-Seite entsteht.
  //
  // NICHT die GitHub-Releases — dort steht als „Latest" noch v0.1.2 vom
  // April 2026, während die Installer über Bunny CDN gehen
  // (`.claude/tasks/profilmenue-plattformwechsel.md`).
  const handleOpenInBrowser = useCallback(() => {
    window.api?.openExternal?.(WEB_APP_URL)
  }, [])

  const handleGetDesktopApp = useCallback(() => {
    window.open(DOWNLOAD_URL, '_blank', 'noopener,noreferrer')
  }, [])

  const handleLogout = useCallback(async () => {
    try {
      const result = await logout()
      if (result.success) {
        if (window.api?.invoke) {
          await window.api.invoke('transition-to-setup')
        } else {
          navigate('/', { replace: true })
        }
      }
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }, [logout, navigate])

  // Offline users have no account to "log out" of. Instead they get a path to
  // connect a HappyCloud account: navigate to the login/register page IN THE
  // CURRENT window. We deliberately do NOT open a separate setup window
  // (transition-to-setup) — that spawns a fresh renderer with its own
  // sessionStorage, so the forceSetupPage flag would be lost and SetupRoute
  // would bounce the offline user straight back into the app. forceSetupPage
  // makes SetupRoute show the login page instead of redirecting; local data
  // stays intact (Phase 3 wires up the actual upload on successful login).
  const handleConnectCloud = useCallback(() => {
    sessionStorage.setItem('forceSetupPage', 'true')
    navigate('/', { replace: true })
  }, [navigate])

  // Persists via platform API (Electron main process / web localStorage) — calling
  // i18n.changeLanguage directly would only update React state and revert on the
  // next platform sync.
  const handleLanguageChange = useCallback((code: string) => {
    changeLanguage(code)
  }, [changeLanguage])

  // activeLanguage can be 'de-DE' etc. — match by primary subtag only.
  const activeLanguageCode = activeLanguage?.split('-')[0] ?? 'en'

  const userName = fullName
    || user?.user_metadata?.full_name
    || user?.full_name
    || t('userMenu.defaultName')
  // Prefer the live Supabase session user — the `user` prop comes from
  // useGlobalUser, which is missing email because the main-process
  // `auth:success` IPC is never emitted (see useUserDisplayProfile).
  const userEmail = authUser?.email || user?.email || ''
  const settingsShortcut = shortcutText('⌘,')

  // Track failed remote avatar loads so a 404 / blocked CDN response falls
  // back to the bundled default image instead of showing the broken-image icon.
  const [imageFailed, setImageFailed] = useState(false)
  const remoteSrc = avatarUrl || user?.avatar_url || null
  const useDefault = !remoteSrc || imageFailed

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <WithTooltip content={userName}>
          <button aria-label={userName} className="user-avatar">
            <span className="user-avatar__hbase user-avatar__hbase--hover">
              <HBaseIcon size={32} color="var(--muted)" />
            </span>
            <span className="user-avatar__hbase user-avatar__hbase--pressed">
              <HBaseIcon size={32} color="var(--border)" />
            </span>
            {!isLoading && (
              <img
                src={useDefault ? avatarDefault : (remoteSrc as string)}
                srcSet={useDefault ? `${avatarDefault} 1x, ${avatarDefault2x} 2x` : undefined}
                alt={userName}
                className="user-avatar__image"
                onError={() => setImageFailed(true)}
              />
            )}
          </button>
        </WithTooltip>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="top"
        align="start"
        // The trigger button is 48px but the visible avatar is only 32px (8px
        // padding on each side). sideOffset is measured from the 48px button
        // edge, so a small negative value pulls the menu close to the visible
        // picture (~6px gap) instead of leaving a 16px gap.
        sideOffset={-2}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel>
          <div className="user-avatar__menu-name">{userName}</div>
          {userEmail && <div className="user-avatar__menu-email">{userEmail}</div>}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={handleSettings}>
          <Settings size={16} className="context-menu-content__item-icon" />
          <span>{t('userMenu.settings')}</span>
          <DropdownMenuShortcut>{settingsShortcut}</DropdownMenuShortcut>
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Globe size={16} className="context-menu-content__item-icon" />
            <span>{t('userMenu.language')}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {LANGUAGES.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onSelect={() => handleLanguageChange(lang.code)}
              >
                <span>{lang.name}</span>
                {activeLanguageCode === lang.code && (
                  <Check size={14} className="context-menu-content__item-check" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {isWeb ? (
          <DropdownMenuItem onSelect={handleGetDesktopApp}>
            <Monitor size={16} className="context-menu-content__item-icon" />
            <span>{t('userMenu.getDesktopApp')}</span>
          </DropdownMenuItem>
        ) : (
          <>
            <DropdownMenuItem onSelect={handleOpenInBrowser}>
              <ExternalLink size={16} className="context-menu-content__item-icon" />
              <span>{t('userMenu.openInBrowser')}</span>
            </DropdownMenuItem>

            {/* Im Browser gibt es nichts zu aktualisieren. */}
            <DropdownMenuItem onSelect={handleCheckForUpdates}>
              <RefreshCw size={16} className="context-menu-content__item-icon" />
              <span>{t('userMenu.checkForUpdates')}</span>
            </DropdownMenuItem>
          </>
        )}

        {/* Auch hier, nicht nur in der Titlebar: Dort weicht der Button im schmalen Fenster, und unter Windows und im
            Web gibt es kein Hilfe-Menü, das ihn ersetzt (#296). */}
        <DropdownMenuItem onSelect={openFeedbackForm}>
          <Megaphone size={16} className="context-menu-content__item-icon" />
          <span>{t('userMenu.feedback')}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {isAuthenticated ? (
          <DropdownMenuItem onSelect={handleLogout}>
            <LogOut size={16} className="context-menu-content__item-icon" />
            <span>{t('userMenu.logout')}</span>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={handleConnectCloud}>
            <CloudUpload size={16} className="context-menu-content__item-icon" />
            <span>{t('userMenu.connectCloud')}</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const UserAvatar = React.memo(UserAvatarComponent)
